<script setup>
/**
 * StatisticsView
 * Main statistics dashboard with economic goals and performance charts
 */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import AppButton from '@/ui/components/AppButton.vue'
import EconomicGoalChart from '@/ui/components/statistics/EconomicGoalChart.vue'
import AgentsPerformanceChart from '@/ui/components/statistics/AgentsPerformanceChart.vue'
import ClientsDetailsModal from '@/ui/components/statistics/ClientsDetailsModal.vue'
import GoalsSummarySection from '@/ui/components/statistics/GoalsSummarySection.vue'
import { useStatistics } from '@/core/composables/useStatistics'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'
import { useConfirmDialog } from '@/core/composables/useConfirmDialog'
import { useTutorial } from '@/core/tutorials/useTutorial'
import { isSeenByUser } from '@/core/tutorials/tutorialStorage'
import { formatDateTime } from '@core/utils/formatting.js'

const authStore = useAuthStore()
const { success, error: toastError } = useToast()
const { confirm } = useConfirmDialog()
const {
  loading,
  updating,
  agentEconomicGoal,
  agentsAnalyses,
  agentsList,
  datesUpdated,
  getEconomicGoalByAgent,
  getAllAgentsAnalyses,
  getAgentsList,
  getUpdateTimestamps,
  triggerEconomicGoalUpdate,
  listenToUpdateStatus,
  clearAgentData,
  cleanup,
} = useStatistics()

// Check user role (claim-based)
const { isAdmin, isAgent } = storeToRefs(authStore)
const userAgent = computed(() => authStore.userAgentId)

// Selected agent for admin view
const selectedAgent = ref('')

// The agent ID to highlight in goal tables
const highlightAgentId = computed(() => {
  if (isAdmin.value) return selectedAgent.value || null
  return userAgent.value
})

// Modal state
const showClientsModal = ref(false)

// Dashboard grid collapse state
const isDashboardCollapsed = ref(false)
const dashboardGridRef = ref(null)
const gridHeight = ref(null)

function toggleDashboard() {
  if (!isDashboardCollapsed.value && dashboardGridRef.value) {
    // Capture current height before collapsing
    gridHeight.value = dashboardGridRef.value.scrollHeight + 'px'
  }
  isDashboardCollapsed.value = !isDashboardCollapsed.value
}

// After the grid mounts or data changes, measure its natural height for animation
watch(
  () => [agentEconomicGoal.value, agentsAnalyses.value],
  () => {
    if (dashboardGridRef.value && !isDashboardCollapsed.value) {
      gridHeight.value = dashboardGridRef.value.scrollHeight + 'px'
    }
  },
  { flush: 'post' }
)

// Tutorial: show on first scroll to guide users to click agent row
const { startTutorial } = useTutorial()
const statisticsContentRef = ref(null)
const goalsSectionRef = ref(null)
const showAgentRowTutorial = ref(false)
let scrollTutorialTriggered = false

function handleContentScroll() {
  if (scrollTutorialTriggered) return
  if (!highlightAgentId.value) return

  // Check if already seen
  const userId = authStore.user?.uid
  if (isSeenByUser(userId, 'statistics-agent-row')) return

  scrollTutorialTriggered = true

  // Enable the tutorial directive on the row
  showAgentRowTutorial.value = true

  // Smooth scroll to goals section, then trigger the tutorial
  if (goalsSectionRef.value) {
    goalsSectionRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Wait for scroll + directive registration, then start
  setTimeout(() => {
    startTutorial('statistics-agent-row')
  }, 600)
}

// Status listener unsubscribe
let statusUnsubscribe = null

// Initialize data on mount
onMounted(async () => {
  try {
    // Load agents chart data
    await getAllAgentsAnalyses()
    await getUpdateTimestamps()

    // If agent, load their economic goal
    if (isAgent.value && userAgent.value) {
      await getEconomicGoalByAgent(userAgent.value)
    }

    // If admin, load agents list for dropdown
    if (isAdmin.value) {
      await getAgentsList()
    }
  } catch (err) {
    console.error('Error initializing statistics:', err)
    toastError('שגיאה בטעינת הנתונים')
  }
})

// Cleanup on unmount
onUnmounted(() => {
  cleanup()
  if (statusUnsubscribe) {
    statusUnsubscribe()
  }
})

// Handle agent selection (admin only)
async function handleAgentSelect() {
  if (!selectedAgent.value) {
    clearAgentData()
    return
  }

  try {
    await getEconomicGoalByAgent(selectedAgent.value)
  } catch (err) {
    toastError('שגיאה בטעינת נתוני הסוכן')
  }
}

// Handle clear agent selection
function handleClearAgent() {
  selectedAgent.value = ''
  clearAgentData()
}

// Handle update data (admin only)
async function handleUpdateData() {
  const ok = await confirm({
    title: 'טעינת נתונים מחדש',
    message: 'לטעון את הנתונים מחדש?',
    confirmText: 'טען',
    variant: 'info',
  })
  if (!ok) return

  try {
    // Set up status listener
    statusUnsubscribe = listenToUpdateStatus((status) => {
      if (status.type === 'success') {
        success(status.message)
      } else if (status.type === 'warning') {
        toastError(status.message)
      } else if (status.type === 'error') {
        toastError(status.message)
      }

      // Cleanup listener
      if (statusUnsubscribe) {
        statusUnsubscribe()
        statusUnsubscribe = null
      }
    })

    await triggerEconomicGoalUpdate()
  } catch (err) {
    toastError('שגיאה בעדכון הנתונים')
  }
}

// Handle chart click - show clients modal
function handleChartClick() {
  if (agentEconomicGoal.value) {
    showClientsModal.value = true
  }
}
</script>

<template>
  <div class="statistics-view">
    <!-- Header -->
    <div class="view-header">
      <h1 class="view-title">סטטיסטיקות</h1>
    </div>

    <!-- Admin Controls -->
    <div v-if="isAdmin" class="admin-controls">
      <div class="agent-selector">
        <label>
          <span class="label-text">בחירת סוכן</span>
          <select v-model="selectedAgent" class="agent-select" @change="handleAgentSelect">
            <option value="">בחר סוכן...</option>
            <option v-for="agent in agentsList" :key="agent.id" :value="agent.id">
              {{ agent.name }} ({{ agent.id }})
            </option>
          </select>
        </label>

        <AppButton v-if="selectedAgent" variant="secondary" size="sm" @click="handleClearAgent">
          נקה בחירה
        </AppButton>
      </div>

      <div class="update-controls">
        <AppButton variant="warning" :loading="updating" @click="handleUpdateData">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
          </svg>
          טעינת נתונים
        </AppButton>

        <div class="update-info">
          <small
            >עדכון חודש קודם:
            {{ formatDateTime(datesUpdated.previousMonth, { fallback: 'לא צויין' }) }}</small
          >
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div
      ref="statisticsContentRef"
      class="statistics-content"
      :class="{ loading }"
      @scroll="handleContentScroll"
    >
      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
      </div>

      <!-- Dashboard Grid - Collapsible -->
      <div class="dashboard-wrapper">
        <button class="collapse-toggle" @click="toggleDashboard">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="collapse-icon"
            :class="{ collapsed: isDashboardCollapsed }"
          >
            <path d="M3 3v18h18" />
            <path d="M18 17V9" />
            <path d="M13 17V5" />
            <path d="M8 17v-3" />
          </svg>
          <span class="collapse-label">גרפים</span>
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="chevron-icon"
            :class="{ collapsed: isDashboardCollapsed }"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div
          ref="dashboardGridRef"
          class="dashboard-grid"
          :class="{ collapsed: isDashboardCollapsed }"
          :style="!isDashboardCollapsed && gridHeight ? { maxHeight: gridHeight } : undefined"
        >
          <!-- Economic Goal Chart -->
          <div class="chart-section economic-section">
            <EconomicGoalChart
              v-if="agentEconomicGoal"
              :data="agentEconomicGoal"
              @click="handleChartClick"
            />
            <div v-else class="no-agent-selected">
              <svg
                viewBox="0 0 24 24"
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p v-if="isAdmin">בחר סוכן כדי לצפות ביעד הכלכלי</p>
              <p v-else>אין נתוני יעד כלכלי זמינים</p>
            </div>
          </div>

          <!-- Agents Performance Chart -->
          <div class="chart-section performance-section">
            <AgentsPerformanceChart
              :data="agentsAnalyses"
              :last-updated="datesUpdated.currentMonth"
            />
          </div>
        </div>
      </div>

      <!-- Goals Summary Section - expands when dashboard collapsed -->
      <div ref="goalsSectionRef" class="goals-section" :class="{ expanded: isDashboardCollapsed }">
        <GoalsSummarySection
          :highlight-agent-id="highlightAgentId"
          :show-tutorial="showAgentRowTutorial"
        />
      </div>
    </div>

    <!-- Clients Details Modal -->
    <ClientsDetailsModal
      v-if="showClientsModal && agentEconomicGoal"
      :goal="agentEconomicGoal"
      @close="showClientsModal = false"
    />
  </div>
</template>

<style scoped>
.statistics-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: var(--spacing-md);
  max-width: 1400px;
  margin: 0 auto;
  gap: var(--spacing-md);
}

/* Header */
.view-header {
  margin-bottom: var(--spacing-sm);
}

.view-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

/* Admin Controls */
.admin-controls {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-lg);
  justify-content: space-between;
  align-items: flex-end;
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
}

.agent-selector {
  display: flex;
  align-items: flex-end;
  gap: var(--spacing-sm);
}

.agent-selector label {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.label-text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.agent-select {
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  min-width: 200px;
}

.agent-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.update-controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.update-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.update-info small {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

/* Content */
.statistics-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  position: relative;
  overflow-y: auto;
}

.statistics-content.loading {
  opacity: 0.6;
  pointer-events: none;
}

.loading-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Dashboard Wrapper */
.dashboard-wrapper {
  display: flex;
  flex-direction: column;
}

/* Collapse Toggle Button */
.collapse-toggle {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  cursor: pointer;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-fast);
  width: fit-content;
  font-family: inherit;
}

.collapse-toggle:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-color: var(--color-primary-light);
}

.collapse-toggle:active {
  transform: scale(0.98);
}

.collapse-label {
  user-select: none;
}

.collapse-icon {
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity var(--transition-fast);
}

.collapse-icon.collapsed {
  opacity: 0.4;
}

.chevron-icon {
  flex-shrink: 0;
  transition: transform 0.3s ease;
}

.chevron-icon.collapsed {
  transform: rotate(-90deg);
}

/* Dashboard Grid */
.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(400px, 2fr);
  gap: var(--spacing-md);
  align-items: stretch;
  margin-top: var(--spacing-sm);
  overflow: hidden;
  transition:
    max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease,
    margin-top 0.3s ease;
  opacity: 1;
}

.dashboard-grid.collapsed {
  max-height: 0 !important;
  opacity: 0;
  margin-top: 0;
  pointer-events: none;
}

.chart-section {
  min-height: 320px;
  height: auto;
}

.economic-section {
  display: flex;
  align-items: stretch;
}

.economic-section > * {
  flex: 1;
  height: 100%;
}

.performance-section {
  display: flex;
  align-items: stretch;
}

.no-agent-selected {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  gap: var(--spacing-md);
  color: var(--text-muted);
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  text-align: center;
}

.no-agent-selected svg {
  opacity: 0.5;
}

.no-agent-selected p {
  margin: 0;
  font-size: var(--font-size-base);
}

/* Goals Section */
.goals-section {
  margin-top: var(--spacing-md);
  transition: margin-top 0.3s ease;
}

.goals-section.expanded {
  margin-top: var(--spacing-sm);
}

/* When collapsed, let the goals section inner grid expand */
.goals-section.expanded :deep(.goals-grid) {
  max-height: none;
}

/* Responsive - Tablet (keep charts in row) */
@media (max-width: 1024px) {
  .dashboard-grid {
    grid-template-columns: minmax(250px, 1fr) minmax(350px, 2fr);
  }
}

/* Responsive - Small Tablet */
@media (max-width: 850px) {
  .dashboard-grid {
    grid-template-columns: 1fr 1.5fr;
    gap: var(--spacing-sm);
  }

  .chart-section {
    min-height: 280px;
  }
}

/* Responsive - Mobile (stack charts) */
@media (max-width: 680px) {
  .statistics-view {
    padding: var(--spacing-sm);
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
  }

  .chart-section {
    min-height: 300px;
  }

  .admin-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .agent-selector {
    flex-direction: column;
    align-items: stretch;
  }

  .agent-select {
    width: 100%;
  }

  .update-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .collapse-toggle {
    padding: var(--spacing-xs) var(--spacing-sm);
    font-size: var(--font-size-xs);
  }
}
</style>
